import { useCallback } from "react";
import { t } from "ttag";

import { useGetPasswordResetTokenStatusQuery } from "metabase/api";
import { Link } from "metabase/common/components/Link";
import { useValidatePassword } from "metabase/common/hooks";
import { useToast } from "metabase/common/hooks/use-toast";
import { useDispatch } from "metabase/redux";
import { resetPassword } from "metabase/redux/auth";
import { useNavigate, useParams, useSearchParams } from "metabase/router";
import { Box, Button, Flex, Title } from "metabase/ui";
import * as Urls from "metabase/urls";

import type { ResetPasswordData } from "../../types";
import { AuthLayout } from "../AuthLayout";
import { ResetPasswordForm } from "../ResetPasswordForm";

type ResetPasswordQueryParams = {
  token: string;
  email: string;
};

export const ResetPassword = (): JSX.Element | null => {
  const [searchParams] = useSearchParams();
  const { token = "" } = useParams<ResetPasswordQueryParams>();
  const redirectUrl = searchParams.get("redirect") ?? undefined;
  const email = searchParams.get("email") ?? undefined;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [sendToast] = useToast();
  const validatePassword = useValidatePassword();
  const { data: status, isLoading } =
    useGetPasswordResetTokenStatusQuery(token);

  const handlePasswordSubmit = useCallback(
    async ({ password }: ResetPasswordData) => {
      const { sessionCreated } = await dispatch(
        resetPassword({ token, password }),
      ).unwrap();
      navigate(sessionCreated ? redirectUrl || "/" : Urls.login(redirectUrl), {
        replace: true,
      });
      sendToast({ message: t`You've updated your password.` });
    },
    [token, dispatch, redirectUrl, sendToast, navigate],
  );

  if (isLoading) {
    return <AuthLayout />;
  }

  return (
    <AuthLayout>
      {status?.valid ? (
        <ResetPasswordForm
          onValidatePassword={validatePassword}
          onSubmit={handlePasswordSubmit}
        />
      ) : (
        <ResetPasswordExpired email={email} />
      )}
    </AuthLayout>
  );
};

interface ResetPasswordExpiredProps {
  email?: string;
}

const ResetPasswordExpired = ({
  email,
}: ResetPasswordExpiredProps): JSX.Element => {
  const forgotPasswordUrl = email
    ? `/auth/forgot_password?email=${encodeURIComponent(email)}`
    : "/auth/forgot_password";

  return (
    <Flex direction="column" align="center">
      <Title
        order={1}
        size="h3"
        c="text-primary"
        ta="center"
        mb="lg"
      >{t`Whoops, that's an expired link`}</Title>
      <Box c="text-primary" ta="center" mb="xxxl">
        {t`For security reasons, password reset links expire after a little while. If you still need to reset your password, you can request a new reset email.`}
      </Box>
      <Button component={Link} variant="filled" to={forgotPasswordUrl}>
        {t`Request a new reset email`}
      </Button>
    </Flex>
  );
};
